import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './UserProfile.css';

const UserProfile = ({ onClose, addToast, confirmAction, vision, accountName }) => {
  const { user, logout, changePassword, authFetch, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading]     = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [message, setMessage]     = useState({ type: '', text: '' });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState(''); // FIX #5: Exige senha para deletar
  const deleteKeyword = 'DeLeTe';

  // ── Edição de nome ──────────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName]         = useState(user?.name || '');
  const [nameLoading, setNameLoading] = useState(false);


  const handleSaveName = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (trimmed.length < 2)  return addToast('Nome deve ter no mínimo 2 caracteres.', 'error');
    if (trimmed.length > 100) return addToast('Nome deve ter no máximo 100 caracteres.', 'error');
    setNameLoading(true);
    try {
      const res = await authFetch('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao salvar nome');
      addToast('Nome atualizado com sucesso!', 'success');
      setEditingName(false);
      await refreshUser(); // Atualiza user no contexto/localStorage
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setNameLoading(false);
    }
  };

  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email ? email[0].toUpperCase() : '?';
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwords.newPassword !== passwords.confirmPassword) {
      return setMessage({ type: 'error', text: 'As novas senhas não coincidem' });
    }
    if (passwords.newPassword.length < 6) {
      return setMessage({ type: 'error', text: 'A nova senha deve ter mínimo 6 caracteres' });
    }

    setLoading(true);
    try {
      await changePassword(passwords.currentPassword, passwords.newPassword);
      addToast('Senha alterada com sucesso!', 'success');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      addToast(err.message, 'error');
    } finally { setLoading(false); }
  };

  const handleSendEmail = async () => {
    // ... codigo de envio de email mantido intacto ...
    setEmailSending(true);
    try {
      const businessId = vision?.startsWith('business:') ? vision.split(':')[1] : null;
      const res  = await authFetch('/api/email/report', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, accountName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(`Relatório enviado para ${user.email}!`, 'success');
    } catch (err) {
      addToast('Erro ao enviar e-mail: ' + err.message, 'error');
    } finally { setEmailSending(false); }
  };

  const handleApplyTheme = (hex) => {
    localStorage.setItem('wf_accent_color', hex);
    document.documentElement.style.setProperty('--accent', hex);
    document.documentElement.style.setProperty('--accent-hover', `${hex}dd`);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirm !== deleteKeyword) return;
    if (!deletePassword) {
      addToast('Digite sua senha para confirmar a exclusão.', 'error');
      return;
    }

    confirmAction('TEM CERTEZA ABSOLUTA? Esta ação não pode ser desfeita e todos os seus dados financeiros serão apagados para sempre.', async () => {
      setLoading(true);
      try {
        const res = await authFetch('/api/auth/request-delete-account', {
          method: 'POST',
          body: JSON.stringify({ password: deletePassword }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Falha ao solicitar exclusão da conta');
        
        addToast(data.message || 'Enviamos um e-mail para confirmar a exclusão da sua conta.', 'success');
        setDeleteConfirm('');
        setDeletePassword('');
        onClose(); // Fecha o modal após o sucesso da solicitação
      } catch (err) {
        addToast('Erro: ' + err.message, 'error');
      } finally {
        setLoading(false);
      }
    });
  };


  const themeColors = [
    { name: 'Azul', hex: '#3b82f6' },
    { name: 'Roxo', hex: '#8b5cf6' },
    { name: 'Índigo', hex: '#4f46e5' },
    { name: 'Verde', hex: '#10b981' },
    { name: 'Laranja', hex: '#f97316' },
    { name: 'Rosa', hex: '#ec4899' },
  ];

  return (
    <div className="profile-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="profile-modal">
        {/* Header */}
        <div className="profile-header">
          <h2>Minha Conta</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Avatar e info */}
        <div className="profile-user-card">
          <div className="profile-avatar">
            {getInitials(user?.name, user?.email)}
          </div>
          <div className="profile-user-info">
            <span className="profile-user-name">{user?.name || 'Usuário'}</span>
            <span className="profile-user-email">{user?.email}</span>
            <span className={`profile-user-role ${user?.role}`}>
              {user?.role === 'admin' ? '👑 Admin' : '👤 Usuário'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          <button className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>Perfil</button>
          <button className={`profile-tab ${activeTab === 'preferences' ? 'active' : ''}`} onClick={() => setActiveTab('preferences')}>Preferências</button>
          <button className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>Segurança</button>
          <button className={`profile-tab ${activeTab === 'danger' ? 'active' : 'danger-tab'}`} onClick={() => setActiveTab('danger')}>Conta</button>
        </div>


        {/* Conteúdo das tabs */}
        {activeTab === 'profile' && (
          <div className="profile-content">
            <div className="profile-info-item">
              <label>Nome</label>
              {editingName ? (
                <form onSubmit={handleSaveName} style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    maxLength={100}
                    autoFocus
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
                  />
                  <button type="submit" disabled={nameLoading} className="profile-btn-save" style={{ padding: '6px 12px', fontSize: 12 }}>
                    {nameLoading ? '...' : 'Salvar'}
                  </button>
                  <button type="button" onClick={() => { setEditingName(false); setNewName(user?.name || ''); }} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}>
                    Cancelar
                  </button>
                </form>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>{user?.name || '—'}</span>
                  <button onClick={() => { setNewName(user?.name || ''); setEditingName(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 12, padding: 0 }} title="Editar nome">✏️ Editar</button>
                </div>
              )}
            </div>
            <div className="profile-info-item">
              <label>Email</label>
              <span>{user?.email}</span>
            </div>
            <div className="profile-info-item">
              <label>Função</label>
              <span className={`profile-badge ${user?.role}`}>
                {user?.role === 'admin' ? 'Administrador' : 'Usuário'}
              </span>
            </div>
            <div className="profile-info-item" style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ width: '100%' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>📧 Relatório Mensal por E-mail</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Receba um resumo completo das suas finanças do mês.</div>
                {message.text && (
                  <div className={`profile-message ${message.type}`} style={{ marginBottom: 8 }}>{message.text}</div>
                )}
                <button
                  className="profile-btn-save"
                  onClick={handleSendEmail}
                  disabled={emailSending}
                  style={{ fontSize: 13, padding: '8px 16px' }}
                >
                  {emailSending ? '⏳ Enviando...' : '📤 Enviar Relatório Agora'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PREFERÊNCIAS (TEMAS) */}
        {activeTab === 'preferences' && (
          <div className="profile-content">
            <h3 className="profile-section-title">Tema Visual (Cor Principal)</h3>
            <p style={{fontSize: 13, color: 'var(--text-muted)', marginBottom: 16}}>
              Escolha uma cor de destaque para os elementos gráficos e botões.
            </p>
            <div className="theme-colors-grid">
              {themeColors.map(c => (
                <button
                  key={c.hex}
                  className="theme-color-btn"
                  style={{ background: c.hex }}
                  onClick={() => handleApplyTheme(c.hex)}
                  title={c.name}
                />
              ))}
            </div>
            
            <button className="theme-reset-btn" onClick={() => handleApplyTheme('#3b82f6')}>
              Restaurar Original
            </button>
          </div>
        )}

        {activeTab === 'security' && (
          <form className="profile-content" onSubmit={handlePasswordChange}>
            <h3 className="profile-section-title">Alterar Senha</h3>

            <div className="profile-field">
              <label>Senha atual</label>
              <input
                type="password"
                placeholder="Sua senha atual"
                value={passwords.currentPassword}
                onChange={(e) => setPasswords(p => ({ ...p, currentPassword: e.target.value }))}
                required
              />
            </div>
            <div className="profile-field">
              <label>Nova senha</label>
              <input
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={passwords.newPassword}
                onChange={(e) => setPasswords(p => ({ ...p, newPassword: e.target.value }))}
                required
              />
            </div>
            <div className="profile-field">
              <label>Confirmar nova senha</label>
              <input
                type="password"
                placeholder="Repita a nova senha"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords(p => ({ ...p, confirmPassword: e.target.value }))}
                required
              />
            </div>

            {message.text && (
              <div className={`profile-message ${message.type}`}>
                {message.type === 'success' ? '✅' : '⚠️'} {message.text}
              </div>
            )}

            <button type="submit" className="profile-btn-save" disabled={loading}>
              {loading ? 'Salvando...' : 'Alterar senha'}
            </button>
          </form>
        )}

        {activeTab === 'danger' && (
          <div className="profile-content danger-zone">
            <h3 className="danger-title">🚨 Zona de Perigo</h3>
            <div className="danger-box">
              <p>Ao excluir sua conta, **todos** os seus dados (transações, metas, orçamentos, cartões e configurações) serão removidos permanentemente do nosso banco de dados.</p>
              <p className="danger-warning">Esta ação é irreversível.</p>
              
              <div className="profile-field" style={{ marginTop: 15 }}>
                <label>Para confirmar, digite exatamente <strong>{deleteKeyword}</strong> abaixo:</label>
                <input
                  type="text"
                  placeholder="Digite a palavra de confirmação"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="danger-input"
                />
              </div>

              {/* FIX #5 (Auditoria): Campo de senha obrigatório para confirmar exclusão */}
              <div className="profile-field" style={{ marginTop: 10 }}>
                <label>Digite sua senha atual para confirmar:</label>
                <input
                  type="password"
                  placeholder="Sua senha atual"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="danger-input"
                />
              </div>

              {message.text && message.type === 'error' && (
                <div className="profile-message error" style={{ marginTop: 10 }}>{message.text}</div>
              )}

              <button 
                onClick={handleDeleteAccount} 
                className="btn-danger-execute"
                disabled={deleteConfirm !== deleteKeyword || !deletePassword || loading}
              >
                {loading ? 'Excluindo...' : 'Sim, excluir minha conta permanentemente'}
              </button>
            </div>
          </div>
        )}


        {/* Footer com logout */}
        <div className="profile-footer">
          <button className="profile-btn-logout" onClick={logout}>
            <span>🚪</span> Sair da conta
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
